// ABOUTME: Hook for product price comparison using tRPC imperative fetch.
// ABOUTME: Fetches comparison data on demand and computes price statistics locally.

import { useState, useCallback } from 'react'
import { trpc } from '@/lib/trpc'
import { calculatePriceStatistics } from '@/lib/price-utils'
import { useStore } from '@/contexts/StoreContext'
import type {
    Product,
    ProductWithPrices,
    Competitor,
    PriceStatistics,
    PriceHistoryPoint,
    SimilarProduct
} from '@/types'

/**
 * Convert SimilarProduct to Competitor format for backward compatibility
 */
function similarProductToCompetitor(similar: SimilarProduct): Competitor {
    return {
        product: {
            id: similar.id,
            name: similar.name,
            brand: similar.brand || 'Unknown Brand',
            category: '',  // Not available from comparison API
            imageUrl: similar.imageUrl || '',
            rating: similar.rating ?? undefined,
            reviewCount: similar.ratingCount ?? undefined,
            unit: similar.unit ?? undefined,
        },
        prices: [{
            platform: 'blinkit',
            price: similar.price,
            mrp: similar.mrp || similar.price,
            discount: similar.discountPercent || 0,
            inStock: similar.inStock,
            deliveryTime: '10 mins',
        }],
        relevanceScore: 80,
    }
}

export function useProductComparison() {
    const { selectedStore } = useStore()
    const [selectedProduct, setSelectedProduct] = useState<ProductWithPrices | null>(null)
    const [competitors, setCompetitors] = useState<Competitor[]>([])
    const [priceStats, setPriceStats] = useState<PriceStatistics | null>(null)
    const [priceHistoryData, setPriceHistoryData] = useState<PriceHistoryPoint[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const utils = trpc.useUtils()

    const loadComparison = useCallback(async (product: Product) => {
        setIsLoading(true)
        try {
            const comparisonData = await utils.comparison.getWithSimilar.fetch({
                productId: product.id,
                lat: selectedStore.latitude,
                lon: selectedStore.longitude,
            })

            if (comparisonData) {
                // Convert the main product to ProductWithPrices format
                const mainProduct = comparisonData.product
                const productWithPrices: ProductWithPrices = {
                    product: {
                        id: mainProduct.id,
                        name: mainProduct.name,
                        brand: mainProduct.brand || product.brand || 'Unknown Brand',
                        category: product.category || '',
                        imageUrl: mainProduct.imageUrl || product.imageUrl || '',
                        rating: mainProduct.rating ?? undefined,
                        reviewCount: mainProduct.ratingCount ?? undefined,
                        unit: mainProduct.unit ?? undefined,
                    },
                    prices: [{
                        platform: 'blinkit',
                        price: mainProduct.price,
                        mrp: mainProduct.mrp || mainProduct.price,
                        discount: mainProduct.discountPercent || 0,
                        inStock: mainProduct.inStock,
                        deliveryTime: '10 mins',
                    }],
                }
                setSelectedProduct(productWithPrices)

                // Convert similar products to competitors format
                const competitorResults = comparisonData.similarProducts.map(similarProductToCompetitor)
                setCompetitors(competitorResults)

                // Calculate stats from the similar products
                if (productWithPrices.prices.length > 0) {
                    const stats = calculatePriceStatistics(
                        productWithPrices.prices[0].price,
                        competitorResults
                    )
                    setPriceStats(stats)
                }
            } else {
                // Fallback: use the product data passed in directly
                setSelectedProduct({ product, prices: [] })
                setCompetitors([])
                setPriceStats(null)
            }

            // Note: Price history is not available from the comparison API
            setPriceHistoryData([])
        } catch {
            // Fallback on error
            setSelectedProduct({ product, prices: [] })
            setCompetitors([])
            setPriceStats(null)
            setPriceHistoryData([])
        } finally {
            setIsLoading(false)
        }
    }, [selectedStore, utils])

    const clearComparison = useCallback(() => {
        setSelectedProduct(null)
        setCompetitors([])
        setPriceStats(null)
        setPriceHistoryData([])
    }, [])

    return {
        selectedProduct,
        competitors,
        priceStats,
        priceHistoryData,
        isLoading,
        loadComparison,
        clearComparison,
    }
}
